import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { usePointTo } from "../../../agent";
import { refusalText } from "../../../api/errors";
import { useApiQuery } from "../../../api/hooks";
import type { DeliverableFilter, Drawn, ElementView, FieldOnPage, ItemPage as ItemPageView, Phase } from "../../../api/types";
import { useCurrentMe } from "../../../app/auth";
import { PartsStrip } from "../../../app/PartsStrip";
import { uploadPath } from "../../../app/paths";
import { useReportLocation } from "../../../live";
import { Banner, Breadcrumb, Button, Card, EmptyState, Loading, NeededByLine, Pill, ShapeByCode, SheetIcon, Tabs } from "../../../ui";
import { ElementActions } from "../components/ElementActions";
import { PhaseDialog } from "../components/PhaseDialog";
import { ProposedHereCard } from "../components/ProposedHere";
import { LockBanner } from "../components/LockBanner";
import { Badges, ElementMarks, LineList } from "../components/marks";
import { ChecksPanel, ElementLink, HistoryPanel, NotesPanel } from "../components/panels";
import { Select } from "../components/fields";
import { fieldLook, requiredFromWords, unitWords } from "../lib/filters";
import { elementBadges, stateWords } from "../lib/badges";
import { chapterPath, elementPath, libraryPath, symbolsPath } from "../lib/paths";
import { FIELD_ORIGIN_WORDS, LINK_TYPE_WORDS, PHASES } from "../lib/words";

/** The screen at /items/:id: the calm document of one item. */
export function ItemPage() {
  const { id = "" } = useParams();
  const page = useApiQuery("item_page", { id }, { enabled: !!id });
  const element = useApiQuery("element", { id }, { enabled: !!id });
  useReportLocation({ screen: "item_page", itemId: id });

  if (page.isPending) return <Loading>Reading the item</Loading>;
  if (page.isError) {
    return (
      <Card>
        <EmptyState title="This item could not be opened">{refusalText(page.error)}</EmptyState>
      </Card>
    );
  }
  return <ItemContent page={page.data} view={element.data} />;
}

function ItemContent({ page, view }: { page: ItemPageView; view: ElementView | undefined }) {
  const me = useCurrentMe();
  const editable = me.active?.roles.includes("engineer") ?? false;
  const item = page.item;
  const [deliverableKey, setDeliverableKey] = useState("");
  const [phase, setPhase] = useState<Phase | "">("");
  const [pointed, setPointed] = useState<{ id: string | null; sentence: string } | null>(null);
  const deliverable: DeliverableFilter | null = page.filters.deliverables.find((d) => d.key === deliverableKey) ?? null;

  usePointTo(
    (event) => {
      setPointed({ id: event.element_id ?? null, sentence: event.sentence });
    },
    { screens: ["item_page"] },
  );

  const groups = useMemo(() => groupFields(page.fields), [page.fields]);
  const labels = useMemo(() => new Map(page.filters.deliverables.map((d) => [d.key, d.label] as const)), [page.filters.deliverables]);

  return (
    <div className="stack item-screen">
      <Card aria-labelledby="item-title">
        <Breadcrumb crumbs={page.breadcrumb} />
        <h1 id="item-title">{item.name}</h1>
        <p className="element-meta">
          <code>{item.code}</code> <ElementMarks element={item} />
          {item.your_own_work && <Pill>Your own work</Pill>}
          {item.origin === "loaded" && <Pill tone="loaded">Loaded{item.discipline ? `, ${item.discipline}` : ""}</Pill>}
          {item.content.abstract === true && <Pill>A parent item, never drawn</Pill>}
          {item.content.is_connection === true && <Pill>A Connection</Pill>}
        </p>
        {item.definition ? <p className="definition">{item.definition}</p> : <p className="muted">No definition yet.</p>}
        <NeededByLine neededBy={item.needed_by} />
        <LockBanner elementId={item.id} lock={view?.lock} />
        {pointed && (
          <Banner tone="demo">
            <p className="agent-text">{pointed.sentence}</p>
          </Banner>
        )}
        <ElementActions element={item} editable={editable} usedIn={view?.used_in} />
        <p className="row quiet-links">
          <Link to={`/items/${encodeURIComponent(item.id)}/map`}>Open its live map</Link>
          {editable && <Link to={`/sessions/new?item=${encodeURIComponent(item.id)}`}>Start a session on it</Link>}
          <Link to={libraryPath({ q: item.name, element: item.id })}>Find it in the stored libraries</Link>
          {editable && <Link to={symbolsPath(item.id)}>Pick its symbol</Link>}
        </p>
      </Card>

      <div className="item-layout">
        <div className="item-layout__main stack">
          <DrawnCard drawn={page.drawn} itemId={item.id} editable={editable} isConnection={item.content.is_connection === true} />
          {page.parts.parts.length > 0 && (
            <Card aria-label="Parts of this item">
              <PartsStrip parts={page.parts} itemId={item.id} editable={editable} />
            </Card>
          )}

          <Card aria-labelledby="fields-title">
            <h2 id="fields-title">Data fields</h2>
            <div className="filter-bar row" role="group" aria-label="Filters">
              <Select
                label="Highlight what a deliverable needs"
                value={deliverableKey}
                placeholder="No deliverable"
                options={page.filters.deliverables.map((d) => ({ value: d.key, label: d.kind ? `${d.label} (kind ${d.kind})` : d.label }))}
                onChange={setDeliverableKey}
              />
              <Select
                label="Fade what is not yet required at"
                value={phase}
                placeholder="Any phase"
                options={(page.filters.phases.length > 0 ? page.filters.phases : PHASES).map((p) => ({ value: p, label: p }))}
                onChange={(value) => setPhase(value as Phase | "")}
              />
            </div>
            {page.fields.length === 0 ? (
              <EmptyState
                title="No data fields yet"
                action={
                  editable ? (
                    <Link className="button button--secondary" to="/elements/new?kind=data_field">
                      Add a data field
                    </Link>
                  ) : undefined
                }
              >
                Fields come from this item, its parent items, its capabilities and its component kinds. None has been added.
              </EmptyState>
            ) : (
              groups.map((group) => (
                <section key={group.key} className="field-group" aria-label={group.name}>
                  <h3>{group.name}</h3>
                  <ul className="field-list">
                    {group.fields.map((entry) => (
                      <FieldRow
                        key={entry.field.id}
                        entry={entry}
                        deliverable={deliverable}
                        phase={phase || null}
                        pointed={pointed?.id === entry.field.id}
                        labels={labels}
                        itemId={item.id}
                        editable={editable}
                        phases={page.filters.phases}
                      />
                    ))}
                  </ul>
                </section>
              ))
            )}
          </Card>

          <Card aria-labelledby="mapping-title">
              <h2 id="mapping-title">Library mapping lines</h2>
              {page.mapping_lines.length === 0 ? (
                <EmptyState title="No library mapping yet">
                  Every item records a match in a stored library, or &quot;no match&quot; with a reason.{" "}
                  <Link to={libraryPath({ q: item.name, element: item.id })}>Find it in the libraries</Link>
                </EmptyState>
              ) : (
                <LineList lines={page.mapping_lines} element={item} headings={new Set(page.mapping_lines.map((l) => l.kind)).size > 1} />
              )}
          </Card>
        </div>

        <aside className="item-layout__side stack" aria-label="Components, documents and places">
          <ProposedHereCard proposals={page.faint_proposals ?? []} meId={me.active?.membership_id} />
          <ComponentsCard components={page.components} editable={editable} phases={page.filters.phases} />
          <DocumentsCard documents={page.documents} editable={editable} />
          <PlacesCard places={page.places} />
          <ChaptersCard chapters={page.chapters} />
        </aside>
      </div>

      {view && (
        <Card>
          <Tabs
            label="More about this item"
            tabs={[
              { id: "notes", label: "Notes", count: view.notes.length, panel: <NotesPanel elementId={item.id} notes={view.notes} canNote /> },
              { id: "checks", label: "Checks", count: view.checks.length, panel: <ChecksPanel checks={view.checks} /> },
              { id: "history", label: "History", count: view.history.length, panel: <HistoryPanel history={view.history} /> },
            ]}
          />
        </Card>
      )}
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ drawn

function DrawnCard({ drawn, itemId, editable, isConnection }: { drawn: Drawn[]; itemId: string; editable: boolean; isConnection: boolean }) {
  return (
    <Card aria-labelledby="drawn-title">
      <h2 id="drawn-title">Where it is drawn</h2>
      {drawn.length === 0 ? (
        <EmptyState title="Not drawn anywhere yet">No kind of drawing shows this item. A parent item that is never drawn has no symbol.</EmptyState>
      ) : (
        <ul className="drawn-list">
          {drawn.map((entry) => (
            <DrawnRow key={entry.document_kind_id} entry={entry} itemId={itemId} editable={editable} isConnection={isConnection} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function DrawnRow({ entry, itemId, editable, isConnection }: { entry: Drawn; itemId: string; editable: boolean; isConnection: boolean }) {
  const mark = entry.symbol ?? entry.line_style;
  const isGeometric = entry.deliverable_kind === "C";
  return (
    <li className="drawn" data-not-drawn={entry.not_drawn || undefined}>
      <SheetIcon kind={entry.deliverable_kind} label={entry.name} />
      <div className="drawn__text">
        <strong>{entry.name}</strong>
        {entry.not_drawn ? (
          <span className="muted"> Not drawn</span>
        ) : isGeometric ? (
          <span className="muted"> Shown on this geometric drawing, with no symbol</span>
        ) : mark ? (
          <span className="drawn__mark">
            <ShapeByCode code={mark.code} kind={entry.line_style ? "line_style" : "symbol"} label={`${entry.line_style ? "Line style" : "Symbol"} ${mark.code}`} size={40} />
            <code>{mark.code}</code>
            {mark.state && <Badges badges={elementBadges({ state: mark.state, origin: "work", pending: null, removal: null, not_used_by_us: null })} />}
          </span>
        ) : (
          <span className="muted">
            {" "}
            No {isConnection ? "line style" : "symbol"} picked yet.{" "}
            {editable && <Link to={symbolsPath(itemId)}>Pick one</Link>}
          </span>
        )}
      </div>
    </li>
  );
}

// ------------------------------------------------------------------------------------------------ fields

interface FieldGroupView {
  key: string;
  name: string;
  rank: number;
  fields: FieldOnPage[];
}

/** The fields in their data groups, ordered by the group's place and the field's place; ungrouped fields last. */
export function groupFields(fields: readonly FieldOnPage[]): FieldGroupView[] {
  const groups = new Map<string, FieldGroupView>();
  for (const entry of fields) {
    const key = entry.group?.id ?? "__ungrouped__";
    let group = groups.get(key);
    if (!group) {
      group = { key, name: entry.group?.name ?? "Other fields", rank: entry.group?.rank ?? Number.MAX_SAFE_INTEGER, fields: [] };
      groups.set(key, group);
    }
    group.fields.push(entry);
  }
  const rankOf = (f: FieldOnPage) => (typeof f.field.content.rank === "number" ? f.field.content.rank : Number.MAX_SAFE_INTEGER);
  const list = [...groups.values()];
  for (const group of list) group.fields.sort((a, b) => rankOf(a) - rankOf(b) || a.field.name.localeCompare(b.field.name));
  list.sort((a, b) => (a.key === "__ungrouped__" ? 1 : b.key === "__ungrouped__" ? -1 : a.rank - b.rank || a.name.localeCompare(b.name)));
  return list;
}

function Lamps({ entry }: { entry: FieldOnPage }) {
  const lamps = entry.phase.lamps;
  return (
    <span className="lamps" role="group" aria-label="Phases in which it is required">
      {PHASES.map((p) => (
        <span key={p} className="lamp" data-on={lamps[p] || undefined} title={`${p}: ${lamps[p] ? "required" : "not required"}`}>
          <span className="lamp__dot" aria-hidden="true" />
          <span className="sr-only">
            {p}: {lamps[p] ? "required" : "not required"}
          </span>
          <span aria-hidden="true">{p.charAt(0)}</span>
        </span>
      ))}
    </span>
  );
}

function FieldRow({
  entry,
  deliverable,
  phase,
  pointed,
  labels,
  itemId,
  editable,
  phases,
}: {
  entry: FieldOnPage;
  deliverable: DeliverableFilter | null;
  phase: Phase | null;
  pointed: boolean;
  labels: ReadonlyMap<string, string>;
  itemId: string;
  editable: boolean;
  phases: readonly Phase[];
}) {
  const [open, setOpen] = useState(false);
  const [setting, setSetting] = useState(false);
  const ref = useRef<HTMLLIElement>(null);
  const look = fieldLook(entry, deliverable, phase);
  const row = entry.field;
  const kind = String(row.content.value_kind ?? "");
  const unit = unitWords(entry.unit, entry.pressure_reference);

  useEffect(() => {
    if (pointed) ref.current?.scrollIntoView?.({ block: "center" });
  }, [pointed]);

  return (
    <li ref={ref} className="field-row" data-look={look} data-pointed={pointed || undefined}>
      <div className="field-row__head">
        <Link className="field-row__name" to={elementPath(row)}>
          {row.name}
        </Link>
        <ElementMarks element={row} />
        {look === "highlight" && deliverable && <Pill>Needed by {deliverable.label}</Pill>}
        {look === "faded" && <span className="muted">Not required yet</span>}
      </div>
      <p className="field-row__facts muted">
        {kind === "project_code" && entry.project_list_kind ? `Code from the project list ${entry.project_list_kind}` : kindWords(kind)}
        {unit ? `, ${unit}` : ""}
        {entry.size_counterpart ? `, with its counterpart ${entry.size_counterpart}` : ""}
        {" · "}from {FIELD_ORIGIN_WORDS[entry.from] ?? entry.from}: {entry.from_element.name}
      </p>
      {entry.project_list_kind && !entry.project_list_loaded && (
        <p className="field-row__warning">
          The project list &quot;{entry.project_list_kind}&quot; is not loaded yet. <Link to="/project-lists">Load it</Link>
        </p>
      )}
      <div className="field-row__phase">
        <Lamps entry={entry} />
        <span className="muted">{requiredFromWords(entry)}</span>
        {entry.phase.state && <span className="muted"> ({stateWords(entry.phase.state)})</span>}
        {editable && (
          <Button variant="quiet" onClick={() => setSetting(true)} aria-label={`Set when it is required: ${row.name}`}>
            Set when it is required
          </Button>
        )}
      </div>
      {setting && <PhaseDialog kind="phase_setting" owner={{ id: row.id, name: row.name }} itemId={itemId} perCase={row.content.per_operating_case === true} phases={phases} onClose={() => setSetting(false)} />}
      {entry.needed_by_deliverables.length > 0 && (
        <p className="field-row__needed muted">Read by {entry.needed_by_deliverables.map((key) => labels.get(key) ?? key).join(", ")}</p>
      )}
      {entry.mapping_lines.length > 0 && (
        <div>
          <Button variant="quiet" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
            {open ? "Hide" : "Show"} {entry.mapping_lines.length} library {entry.mapping_lines.length === 1 ? "line" : "lines"}
          </Button>
          {open && <LineList lines={entry.mapping_lines} element={row} />}
        </div>
      )}
    </li>
  );
}

function kindWords(kind: string): string {
  const words: Record<string, string> = {
    text: "Text",
    whole_number: "Whole number",
    number: "Number",
    choice: "Choice from a pick list",
    yes_no: "Yes or no",
    date: "Date",
    size_designation: "Size designation",
    project_code: "Code from a project list",
    table: "Table",
  };
  return words[kind] ?? (kind ? kind.replace(/_/g, " ") : "Kind of value not said");
}

// ------------------------------------------------------------------------------------------------ side panels

function ComponentsCard({ components, editable, phases }: { components: ItemPageView["components"]; editable: boolean; phases: readonly Phase[] }) {
  const [setting, setSetting] = useState<{ id: string; name: string } | null>(null);
  return (
    <Card aria-labelledby="components-title">
      <h2 id="components-title">Components</h2>
      {components.length === 0 ? (
        <EmptyState
          title="No components yet"
          action={
            editable ? (
              <Link className="button button--secondary" to="/elements/new?kind=component">
                Add a component
              </Link>
            ) : undefined
          }
        >
          A component is a thing inside the item, with its own data and a role name.
        </EmptyState>
      ) : (
        <ul className="component-list">
          {components.map((component) => (
            <li key={component.element.id} data-in-focus={component.in_focus}>
              <Link to={elementPath(component.element)}>{component.element.name}</Link> <span className="muted">{component.kind.name}</span>
              <ElementMarks element={component.element} />
              {component.kind.tie_in_note && <p className="tie-in muted">Tie-in: {component.kind.tie_in_note}</p>}
              {(component.documents ?? []).length > 0 && (
                // The sheets the component is shown on, described by or specified by, each with the letter of its kind (UI-17).
                <ul className="component-sheets" aria-label={`Documents of ${component.element.name}`}>
                  {(component.documents ?? []).map((doc) => (
                    <li key={doc.document_link_id} className="component-sheets__doc">
                      <SheetIcon kind={doc.document_kind.deliverable_kind} label={doc.document_kind.name} size={16} />
                      <span className="muted">
                        {LINK_TYPE_WORDS[doc.link_type]} {doc.document_kind.name}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {editable && (
                <Button variant="quiet" onClick={() => setSetting({ id: component.element.id, name: component.element.name })} aria-label={`Set at least one from a phase: ${component.element.name}`}>
                  At least one from a phase
                </Button>
              )}
              {component.symbols.length > 0 && (
                <p className="muted">
                  Symbols: {component.symbols.map((s) => s.code).join(", ")}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
      {setting && <PhaseDialog kind="at_least_one" owner={setting} phases={phases} onClose={() => setSetting(null)} />}
    </Card>
  );
}

function DocumentsCard({ documents, editable }: { documents: ItemPageView["documents"]; editable: boolean }) {
  return (
    <Card aria-labelledby="documents-title">
      <h2 id="documents-title">Documents</h2>
      {documents.length === 0 ? (
        <EmptyState title="No documents yet">The sheets it is shown on, its datasheets and its specifications show here.</EmptyState>
      ) : (
        <ul className="document-list">
          {documents.map((doc) => (
            <li key={doc.document_link_id} className="document">
              <SheetIcon kind={doc.document_kind.deliverable_kind} label={doc.document_kind.name} />
              <span>
                <Link to={`/elements/${encodeURIComponent(doc.document_kind.id)}`}>{doc.document_kind.name}</Link>{" "}
                <span className="muted">{LINK_TYPE_WORDS[doc.link_type]}</span>
              </span>
              {doc.state && <span className="muted">{stateWords(doc.state)}</span>}
            </li>
          ))}
        </ul>
      )}
      {editable && (
        <p>
          <Link className="button button--secondary" to={uploadPath()}>
            Add a document
          </Link>
        </p>
      )}
    </Card>
  );
}

function PlacesCard({ places }: { places: ItemPageView["places"] }) {
  return (
    <Card aria-labelledby="places-title">
      <h2 id="places-title">Places</h2>
      {places.length === 0 ? (
        <EmptyState title="No place yet">Where it is located and what it rests on show here.</EmptyState>
      ) : (
        <ul className="place-list">
          {places.map((place) => (
            <li key={place.link.id}>
              <Link to={elementPath(place.link)}>{place.link.name}</Link>
              {place.far_end ? (
                <>
                  {" "}
                  <span className="muted">to</span> <ElementLink id={place.far_end.element_id} />
                </>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function ChaptersCard({ chapters }: { chapters: ItemPageView["chapters"] }) {
  if (chapters.length === 0) return null;
  return (
    <Card aria-labelledby="chapters-title">
      <h2 id="chapters-title">Chapters</h2>
      <ul>
        {chapters.map((chapter) => (
          <li key={chapter.key}>
            <Link to={chapterPath(chapter.key)}>{chapter.title}</Link> {chapter.state && <span className="muted">{stateWords(chapter.state)}</span>}
          </li>
        ))}
      </ul>
    </Card>
  );
}
